import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
} from "react";
import { Dayjs } from "dayjs";
import { getAllRoutes, getAllRoutesByCompany } from "../../services";
import { SaleRoute } from "../../types/sale-route.type";
import { Branch } from "../../types/user.type";
import {
  ExpressDataStatus,
  ExpressDataTransactionType,
} from "../../types/express-data.type";
import {
  EXPRESS_DATA_STATUS_OPTIONS,
  EXPRESS_DATA_TRANSACTION_TYPE_OPTIONS,
} from "./utils";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";

interface ExpressDataFilterProps {
  /** Head office and master pick the branch; everyone else reads their own. */
  canSelectBranch: boolean;
  branches: Branch[];
  branchCode: string;
  onChangeBranch: (branchCode: string) => void;
  transactionDate: Dayjs;
  setTransactionDate: Dispatch<SetStateAction<Dayjs>>;
  selectedRoute: string;
  setSelectedRoute: Dispatch<SetStateAction<string>>;
  transactionType: ExpressDataTransactionType | "all";
  setTransactionType: Dispatch<
    SetStateAction<ExpressDataTransactionType | "all">
  >;
  status: ExpressDataStatus[];
  setStatus: Dispatch<SetStateAction<ExpressDataStatus[]>>;
}

const fieldSx = {
  width: "100%",
};

export default function ExpressDataFilter({
  canSelectBranch,
  branches,
  branchCode,
  onChangeBranch,
  transactionDate,
  setTransactionDate,
  selectedRoute,
  setSelectedRoute,
  transactionType,
  setTransactionType,
  status,
  setStatus,
}: Readonly<ExpressDataFilterProps>) {
  const [saleRoutes, setSaleRoutes] = useState<SaleRoute[]>([]);

  const selectedBranch = branches.find((branch) => branch.branch_code === branchCode);
  const selectedCompanyId = selectedBranch?.company_id;

  // A branch picker lists the routes of the branch it names; everyone else lists their own.
  useEffect(() => {
    if (canSelectBranch && !selectedCompanyId) {
      setSaleRoutes([]);
      return;
    }
    let active = true;
    (async () => {
      try {
        const {
          data: { data },
        } = canSelectBranch
          ? await getAllRoutes(selectedCompanyId)
          : await getAllRoutesByCompany();
        if (active) setSaleRoutes(data);
      } catch {
        // The interceptor already shows why the routes could not be read.
        if (active) setSaleRoutes([]);
      }
    })();
    return () => {
      active = false;
    };
  }, [canSelectBranch, selectedCompanyId]);

  const handleChangeBranch = (event: ChangeEvent<HTMLInputElement>) => {
    onChangeBranch(event.target.value);
  };

  return (
    <Box sx={{ display: "grid", gap: 2, width: "100%" }}>
      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            md: "repeat(3, minmax(0, 1fr))",
          },
        }}
      >
        {canSelectBranch && (
          <TextField
            select
            fullWidth
            size="small"
            label="สาขา"
            value={selectedBranch ? branchCode : ""}
            onChange={handleChangeBranch}
            sx={fieldSx}
          >
            {branches.map((branch) => (
              <MenuItem key={branch.uuid} value={branch.branch_code}>
                {branch.branch_code} - {branch.branch_name}
              </MenuItem>
            ))}
          </TextField>
        )}

        <TextField
          select
          fullWidth
          size="small"
          label="สายส่ง"
          value={selectedRoute}
          onChange={(e) => setSelectedRoute(e.target.value)}
          sx={fieldSx}
        >
          <MenuItem value="all">ทั้งหมด</MenuItem>
          {saleRoutes.map((route) => (
            <MenuItem key={route.route_code} value={route.route_code}>
              {route.route_code} - {route.route_name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          fullWidth
          size="small"
          label="ประเภทรายการ"
          value={transactionType}
          onChange={(e) =>
            setTransactionType(
              e.target.value as ExpressDataTransactionType | "all",
            )
          }
          sx={fieldSx}
        >
          <MenuItem value="all">ทั้งหมด</MenuItem>
          {EXPRESS_DATA_TRANSACTION_TYPE_OPTIONS.map((item) => (
            <MenuItem key={item.value} value={item.value}>
              {item.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          fullWidth
          size="small"
          label="สถานะ"
          value={status}
          onChange={(e) =>
            setStatus(e.target.value as unknown as ExpressDataStatus[])
          }
          sx={fieldSx}
          slotProps={{
            inputLabel: { shrink: true },
            select: {
              multiple: true,
              displayEmpty: true,
              renderValue: (selected) => {
                const values = selected as ExpressDataStatus[];
                if (values.length === 0) return "ทั้งหมด";
                return values
                  .map(
                    (v) =>
                      EXPRESS_DATA_STATUS_OPTIONS.find(
                        (o) => o.value === v,
                      )?.label ?? v,
                  )
                  .join(", ");
              },
            },
          }}
        >
          {EXPRESS_DATA_STATUS_OPTIONS.map((item) => (
            <MenuItem key={item.value} value={item.value}>
              <Checkbox checked={status.includes(item.value)} />
              <ListItemText primary={item.label} />
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="th">
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
            },
          }}
        >
          <DatePicker
            value={transactionDate}
            format="DD/MM/YYYY"
            label="วันที่รายการ"
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
              },
            }}
            onChange={(value) => value && setTransactionDate(value)}
          />
        </Box>
      </LocalizationProvider>
    </Box>
  );
}
